import { useCallback, useEffect, useState } from "react";

export function useAsyncResult<T>(options: {
  func: (
    disposable: Disposable,
    force?: boolean,
    onUpdate?: (value: T) => void,
  ) => Promise<T>;
  setError: (error: any) => void;
}) {
  const { func, setError } = options;
  const [value, setValue] = useState<T>();
  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [force, setForce] = useState<boolean | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const refresh = useCallback(() => {
    setForce((value) => !value);
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const disposable: Disposable = {
      isDisposed: false,
      signal: abortController.signal,
    };
    setError(undefined);
    setLoading(true);

    const onUpdate = (partial: T) => {
      if (!disposable.isDisposed) {
        setValue(partial);
      }
    };

    func(disposable, force !== undefined, onUpdate)
      .then((result) => {
        if (!disposable.isDisposed) {
          setValue(result);
          setLastUpdated(new Date());
          setLoading(false);
        }
      })
      .catch((error) => {
        if (!disposable.isDisposed) {
          setError(error);
          setLoading(false);
        }
      });
    return () => {
      disposable.isDisposed = true;
      abortController.abort();
    };
  }, [func, force, setError, setLoading]);

  return {
    value,
    lastUpdated,
    refresh,
    loading,
  };
}

export interface Disposable {
  isDisposed: boolean;
  signal: AbortSignal;
}
